import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';
import { getCookie } from '../../../utils';

import { fetchMealTypesList } from '../optionsSlice';

export const fetchTypes = createAsyncThunk(
  'units/fetchTypes',
  async ({ page = 1, searchTerm="" }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/meal-types-list?page=${page}`, {searchTerm});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch units');
    }
  }
);

export const deleteType = createAsyncThunk(
  'types/deleteType',
  async (typeId, { dispatch, rejectWithValue }) => {
    try {
      const token = getCookie('accessToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/meal-types-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id: typeId }),
      });
      if (!response.ok) {
        throw new Error('Failed to delete type');
      }
      dispatch(fetchTypes({ page: 1 }));
      dispatch(fetchMealTypesList());
      return typeId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete type');
    }
  }
);

export const createType = createAsyncThunk(
  'types/createType',
  async (typeData, { dispatch, rejectWithValue }) => {
    try {
      const token = getCookie('accessToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/meal-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(typeData),
      });

      if (!response.ok) {
        throw new Error('Failed to create type');
      }
      const data = await response.json();
      dispatch(fetchTypes({ page: typeData.page || 1 }));
      dispatch(fetchMealTypesList());
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create type');
    }
  }
);

export const updateType = createAsyncThunk(
  'types/updateType',
  async (typeData, { dispatch, rejectWithValue }) => {
    try {
      const token = getCookie('accessToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/meal-types/${typeData.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(typeData),
      });

      if (!response.ok) {
        throw new Error('Failed to update type');
      }

      const data = await response.json();
      dispatch(fetchTypes({ page: typeData.page || 1 }));
      dispatch(fetchMealTypesList());
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update type');
    }
  }
);

const typesSlice = createSlice({
  name: 'admin_types',
  initialState: {
    types: [],
    currentTypePage: 1,
    totalTypePages: 1,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    setTypePage: (state, action) => {
      state.currentTypePage = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.types = action.payload.data.data;
        state.currentTypePage = action.payload.data.current_page || 1;
        state.totalTypePages = action.payload.data.last_page || 0;
      })
      .addCase(fetchTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteType.fulfilled, (state, action) => {
        state.types = state.types.filter(type => type.id !== action.payload);
      })
      .addCase(deleteType.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createType.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = 'Type created successfully';
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(createType.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateType.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.types.findIndex(type => type.id === action.payload.data.id);
          if (index !== -1) {
            state.types[index] = action.payload.data;
          }
          state.successMessage = 'Type updated successfully';
        } else {
          state.error = action.payload.message
        }
      })
      .addCase(updateType.rejected, (state, action) => {
        state.error = action.payload.message;
      });
  },
});

export const { setTypePage, clearMessages } = typesSlice.actions;

export default typesSlice.reducer;
